import { Calendar, ChevronRight, Lock, Star, Zap } from "lucide-react";
import { PLATFORMS } from "../../constants/scriptConstants";

/**
 * HistoryItemCard Component
 * =========================
 * Displays a single script history item in the history panel.
 */
export const HistoryItemCard = ({ 
  item, 
  onClick, 
  isSelected, 
  onToggleFavorite, 
  canUseFavorites, 
  onShowUpgradeModal 
}) => {
  const platform = PLATFORMS.find(p => p.id === item.platform) || PLATFORMS[0];
  const firstCaption = item.segments?.[0]?.caption || "No caption";
  const createdAt = item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }) : '';

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    if (!canUseFavorites) {
      onShowUpgradeModal?.("favorites");
      return;
    }
    if (item.history_id) {
      onToggleFavorite(item.history_id);
    }
  };

  return (
    <button
      onClick={onClick}
      data-testid={`history-item-${item.platform}`}
      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 hover:scale-[1.02] ${
        isSelected 
          ? 'bg-zinc-800 border-zinc-600' 
          : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Platform badge */}
        <div className={`w-10 h-10 flex-shrink-0 rounded-lg flex items-center justify-center ${
          item.platform === 'tiktok' ? 'bg-black border border-[#25F4EE]' :
          item.platform === 'shorts' ? 'bg-[#FF0000]' :
          'bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45]'
        }`}>
          <Zap className="w-4 h-4 text-white" />
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Platform & time */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className={`text-xs font-bold uppercase ${
              item.platform === 'tiktok' ? 'text-[#25F4EE]' :
              item.platform === 'shorts' ? 'text-[#FF0000]' :
              'text-[#FD1D1D]'
            }`}>
              {platform.name}
            </span>
            <div className="flex items-center gap-2">
              {createdAt && (
                <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {createdAt}
                </span>
              )}
            </div>
          </div>
          
          {/* Idea preview */}
          <p className="text-sm text-zinc-300 line-clamp-2 mb-2">
            {item.idea}
          </p>
          
          {/* First caption preview */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-zinc-500 uppercase">Hook:</span>
            <span className="text-xs text-zinc-400 font-medium truncate">
              {firstCaption}
            </span>
          </div>
          
          {/* Tags */}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] px-2 py-0.5 bg-zinc-800 rounded text-zinc-500 capitalize">
              {item.tone}
            </span>
            <span className="text-[10px] px-2 py-0.5 bg-zinc-800 rounded text-zinc-500">
              {item.category}
            </span>
          </div>
        </div>
        
        {/* Favorite & Arrow */}
        <div className="flex flex-col items-center gap-2 flex-shrink-0">
          {item.history_id && (
            <div
              onClick={handleFavoriteClick}
              data-testid={`favorite-btn-${item.history_id}`}
              title={canUseFavorites ? (item.is_favorite ? 'Remove from favorites' : 'Add to favorites') : 'Click to see upgrade options'}
              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                item.is_favorite 
                  ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30' 
                  : canUseFavorites 
                    ? 'bg-zinc-800 text-zinc-600 hover:text-yellow-400 hover:bg-zinc-700'
                    : 'bg-zinc-800 text-zinc-600 hover:bg-zinc-700 hover:text-violet-400'
              }`}
            >
              {canUseFavorites ? (
                <Star className={`w-4 h-4 ${item.is_favorite ? 'fill-current' : ''}`} />
              ) : (
                <Lock className="w-3.5 h-3.5" />
              )}
            </div>
          )}
          <ChevronRight className="w-4 h-4 text-zinc-600" />
        </div>
      </div>
    </button>
  );
};

export default HistoryItemCard;
